import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Search, ArrowDownUp, Filter, Loader } from 'lucide-react';
import { RestaurantWithSmsStatus } from '@/context/SmsStatusContext';
import { safeToLowerCase, getDisplayName, getEntityId } from '@/utils/display-helpers';

interface UsageReportSelectorProps {
  restaurants: RestaurantWithSmsStatus[];
  loading: boolean;
  onSelectRestaurants: (restaurantIds: string[]) => void;
}

// Helper function to safely get a display name for a restaurant
const getRestaurantDisplayName = (restaurant: RestaurantWithSmsStatus | null | undefined) => {
  if (!restaurant) return 'Unknown Restaurant';
  return getDisplayName(restaurant, 'id', 'name', 'Restaurant');
};

// Helper function to safely get restaurant ID
const getRestaurantId = (restaurant: RestaurantWithSmsStatus | null | undefined): string => {
  if (!restaurant) return 'unknown-id';
  return getEntityId(restaurant, ['id', 'restaurant_id']);
};

const UsageReportSelector: React.FC<UsageReportSelectorProps> = ({
  restaurants,
  loading,
  onSelectRestaurants
}) => {
  const [selectedRestaurants, setSelectedRestaurants] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  
  // Filter restaurants based on search term with null-safe operations
  const filteredRestaurants = useMemo(() => {
    return restaurants
      .filter(restaurant => {
        // Null check before calling toLowerCase
        const restaurantName = restaurant?.name || '';
        const searchTermLower = safeToLowerCase(searchTerm);
        return safeToLowerCase(restaurantName).includes(searchTermLower);
      })
      .sort((a, b) => {
        // Null checks for sort
        const nameA = a?.name || '';
        const nameB = b?.name || '';
        
        if (sortDirection === 'asc') {
          return nameA.localeCompare(nameB);
        } else {
          return nameB.localeCompare(nameA);
        }
      });
  }, [restaurants, searchTerm, sortDirection]);
  
  // Toggle selection for a restaurant
  const toggleRestaurantSelection = (restaurantId: string) => {
    if (!restaurantId) return;
    
    setSelectedRestaurants(prev => {
      if (prev.includes(restaurantId)) {
        return prev.filter(id => id !== restaurantId);
      } else {
        return [...prev, restaurantId];
      }
    });
  };
  
  // Select or deselect all restaurants
  const toggleSelectAll = () => {
    if (selectedRestaurants.length === filteredRestaurants.length) {
      setSelectedRestaurants([]);
    } else {
      // Filter out any undefined/null IDs
      const validIds = filteredRestaurants
        .map(r => r?.id)
        .filter(Boolean) as string[];
      setSelectedRestaurants(validIds);
    }
  };
  
  // Update parent component when selected restaurants change
  useEffect(() => {
    onSelectRestaurants(selectedRestaurants.filter(Boolean));
  }, [selectedRestaurants]);
   
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Select Restaurants for Report</CardTitle>
        <CardDescription>
          Choose restaurants to include in the usage report
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-4">
          {/* Search and sort controls */}
          <div className="flex flex-col sm:flex-row gap-2 justify-between">
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={toggleSelectAll}
              >
                {selectedRestaurants.length === filteredRestaurants.length ? 'Deselect All' : 'Select All'}
              </Button>
              
              {selectedRestaurants.length > 0 && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setSelectedRestaurants([])}
                >
                  Clear selection
                </Button>
              )}
            </div>
            
            <div className="flex gap-2">
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search restaurants..." 
                  className="pl-8"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              
              <Button
                size="icon"
                variant="outline"
                onClick={() => setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')}
                title={`Sort ${sortDirection === 'asc' ? 'descending' : 'ascending'}`}
              >
                <ArrowDownUp className="h-4 w-4" />
              </Button>
            </div>
          </div>
          
          {/* Restaurant list */}
          <div className="rounded-md border max-h-[300px] overflow-y-auto">
            <div className="bg-muted/50 p-3 flex items-center border-b sticky top-0 z-10">
              <div className="flex items-center">
                <Checkbox 
                  id="select-all"
                  checked={selectedRestaurants.length > 0 && selectedRestaurants.length === filteredRestaurants.length}
                  onCheckedChange={toggleSelectAll}
                  className="mr-2"
                />
                <label htmlFor="select-all" className="text-sm font-medium">
                  {selectedRestaurants.length} of {filteredRestaurants.length} restaurants selected
                </label>
              </div>
            </div>
            
            <div className="divide-y">
              {loading ? (
                <div className="flex justify-center items-center py-8">
                  <Loader className="h-6 w-6 animate-spin text-muted-foreground" />
                  <span className="ml-2">Loading restaurants...</span>
                </div>
              ) : filteredRestaurants.length === 0 ? (
                <div className="py-6 text-center text-muted-foreground">
                  No restaurants found.
                </div>
              ) : (
                filteredRestaurants.map(restaurant => {
                  // Safe access to restaurant ID using the utility function
                  const restaurantId = getRestaurantId(restaurant);
                  const restaurantName = getRestaurantDisplayName(restaurant);
                  const isSelected = selectedRestaurants.includes(restaurantId);
                  
                  return (
                    <div 
                      key={restaurantId} 
                      className={`p-3 flex items-center justify-between gap-2 hover:bg-muted/30 ${
                        isSelected ? 'bg-muted/40' : ''
                      }`}
                      onClick={() => toggleRestaurantSelection(restaurantId)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="flex items-center">
                        <Checkbox 
                          id={`restaurant-${restaurantId}`}
                          checked={isSelected}
                          onCheckedChange={() => {}}
                          className="mr-3"
                        />
                        <div>
                          <label htmlFor={`restaurant-${restaurantId}`} className="font-medium cursor-pointer">
                            {restaurantName}
                          </label>
                          {restaurant.id && restaurant.id !== 'unknown-id' && (
                            <p className="text-xs text-muted-foreground">ID: {restaurant.id}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
          
          {/* Selected restaurant info */}
          {selectedRestaurants.length > 0 && (
            <div className="bg-muted/30 p-3 rounded-md text-sm">
              <p className="font-medium">
                {selectedRestaurants.length} restaurant{selectedRestaurants.length !== 1 ? 's' : ''} selected
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Reports will be generated for the selected restaurants
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default UsageReportSelector;